import { Link, useLocation, useNavigate } from 'react-router-dom'
import { 
  IconCirclePlusFilled, 
  IconTag,
  IconTicket,
  IconUserShield,
  IconBuilding,
  IconBuildingStore,
  type Icon 
} from "@tabler/icons-react"
import { PermissionGate } from "@/components/auth/PermissionGate"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

export function NavMain({
  items,
}: {
  items: {
    title: string
    url: string
    icon?: Icon
    permission?: string
  }[]
}) {
  const location = useLocation()
  const navigate = useNavigate()
  
  const quickCreateItems = [
    {
      label: 'Offer',
      icon: IconTag,
      path: '/offers/new',
      permission: 'offer_management:create'
    },
    {
      label: 'Promo Offer',
      icon: IconTicket,
      path: '/promo-offers/new',
      permission: 'promo_management:create'
    },
    {
      label: 'Admin User',
      icon: IconUserShield,
      path: '/admin-users/new',
      permission: 'user_management:create'
    },
    {
      label: 'Organization',
      icon: IconBuilding,
      path: '/organizations/new',
      permission: 'organization_management:create'
    },
    {
      label: 'Venue',
      icon: IconBuildingStore,
      path: '/venues/new',
      permission: 'venue_management:create'
    }
  ]
  
  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          <SidebarMenuItem className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  tooltip="Quick Create"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground active:bg-primary/90 active:text-primary-foreground min-w-8 duration-200 ease-linear"
                  data-testid="quick-create-button"
                >
                  <IconCirclePlusFilled />
                  <span>Quick Create</span>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuLabel>Create New</DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {quickCreateItems.map((item) => {
                  const menuItem = (
                    <DropdownMenuItem
                      key={item.label}
                      onClick={() => navigate(item.path)}
                      data-testid={`create-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className="mr-2 h-4 w-4 text-primary" />
                      {item.label}
                    </DropdownMenuItem>
                  )
                  
                  return item.permission ? (
                    <PermissionGate key={item.label} permission={item.permission}>
                      {menuItem}
                    </PermissionGate>
                  ) : (
                    menuItem
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu>
          {items.map((item) => {
            const isActive = location.pathname === item.url || 
                           (item.url === '/' && location.pathname === '/')
            
            const menuItem = (
              <SidebarMenuItem key={item.title} data-testid={`${item.title.toLowerCase()}-nav`}>
                <SidebarMenuButton 
                  tooltip={item.title} 
                  asChild 
                  isActive={isActive}
                >
                  <Link 
                    to={item.url} 
                    data-testid={`nav-link-${item.title.toLowerCase()}`}
                  >
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )

            // Wrap with permission gate if permission is specified
            return item.permission ? (
              <PermissionGate key={item.title} permission={item.permission}>
                {menuItem}
              </PermissionGate>
            ) : (
              menuItem
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
